import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Fingerprint, ShieldCheck, Tag } from "lucide-react";
import { toast } from "sonner";
import {
  applyProvenAttribution,
  attributeTrade,
  attributeTradesBulk,
  reconcileTrades,
  relabelCtrader,
} from "@/lib/api";
import { InfoHint } from "@/components/InfoHint";

const fmt = (v) => (v == null ? "—" : v > 0 ? `+${v.toFixed(2)}` : v.toFixed(2));
const cls = (v) => (v > 0 ? "text-emerald-300" : v < 0 ? "text-rose-300" : "text-white/40");
const CERT = {
  prova: "border-emerald-500/50 text-emerald-300",
  indizio: "border-amber-500/50 text-amber-300",
  ambigua: "border-white/25 text-white/50",
};

// I padri offerti in linea: gli altri restano nel menu completo del backend.
const QUICK = [
  ["mano-tua", "Mano tua"],
  ["copy", "Copy Telegram"],
  ["copy-hl", "Copy HL"],
  ["poly", "Motore Poly"],
  ["bot", "Bot Gold"],
  ["test", "Prova"],
];
const ROWS = 8;

export const TradeAttributionBlock = ({ data, onDone }) => {
  const [busy, setBusy] = useState(null);
  const [rec, setRec] = useState({});
  const [groups, setGroups] = useState([]);
  const n = data?.n?.orfano || 0;
  useEffect(() => {
    if (!n) return;
    reconcileTrades()
      .then((r) => {
        setRec(Object.fromEntries((r.orphans || []).map((o) => [o.id, o.proposal])));
        setGroups(r.groups || []);
      })
      .catch(() => {});
  }, [n]);
  if (!data) return null;
  const { orphans = [], pnl = {}, n: cnt = {} } = data;

  const run = async (key, fn, ok) => {
    setBusy(key);
    try {
      const r = await fn();
      toast.success(ok(r));
      onDone?.();
    } catch (e) {
      toast.error("Attribuzione fallita", { description: String(e?.message || e) });
    } finally {
      setBusy(null);
    }
  };
  const assign = (id, father, why) =>
    run(`${id}:${father}`, () => attributeTrade(id, father, why || ""), (r) => `Attribuito a ${r.label}`);
  const confirmGroup = (g) =>
    run(`g:${g.key}`, () => attributeTradesBulk(g.ids, g.father, g.why || ""),
      (r) => `${r.n} trade attribuiti a ${r.label}`);
  // «mano tua» non e' applicabile da sola nemmeno provata: e' un'identificazione
  const proven = groups.filter((g) => g.certainty === "prova" && g.father && g.father !== "mano-tua");
  const provenN = proven.reduce((s, g) => s + g.n, 0);

  return (
    <div className="rounded-md border border-amber-500/25 bg-amber-500/[0.04] p-2.5 space-y-2"
      data-testid="attribution-block">
      <div className="flex flex-wrap items-center gap-2">
        <Fingerprint className="h-3.5 w-3.5 text-amber-300" />
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Attribuzione del denaro reale
        </span>
        <InfoHint testId="info-attribution" text={data.rule} />
        <Badge variant="outline"
          className={`text-[10px] ${cnt.orfano > 0 ? "border-amber-500/50 text-amber-300" : "border-emerald-500/40 text-emerald-300"}`}
          data-testid="attribution-badge">
          {cnt.orfano > 0
            ? `${cnt.orfano} senza padre · ${fmt(pnl.orfano || 0)} USD (altre valute separate)`
            : "ogni trade reale ha un padre"}
        </Badge>
        {(data.born?.n || 0) > 0 && (
          <Badge variant="outline" className="border-rose-500/50 text-[10px] text-rose-300"
            title={data.born?.note} data-testid="attribution-born-badge">
            {data.born.n} orfani NATI dal timbro · ultimo {data.born.lastKey}
          </Badge>
        )}
        {provenN > 0 && (
          <Button size="sm" className="h-6 px-2 text-[10px]" disabled={busy === "proven"}
            onClick={() => run("proven", applyProvenAttribution, (r) => `${r.n} prove applicate (${fmt(r.pnl || 0)}$)`)}
            data-testid="attribution-apply-proven">
            <ShieldCheck className="mr-1 h-3 w-3" /> applica le {provenN} prove
          </Button>
        )}
        <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]"
          disabled={busy === "relabel"}
          onClick={() => run("relabel", () => relabelCtrader(120),
            (r) => `${r.rowsRead || 0} righe rilette dal banco, ${r.autoTagged || 0} etichettate`)}
          data-testid="attribution-relabel-ctrader">
          <Tag className="mr-1 h-3 w-3" /> rileggi etichette cTrader
        </Button>
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-0.5 font-mono text-[10.5px] text-white/55"
        data-testid="attribution-fathers">
        {(data.fathers || []).map((f) => (
          <span key={f.key || f.father} data-testid={`attribution-father-${f.key || f.father}`}>
            {f.father === "orfano" ? "senza padre" : f.label} ×{f.n}{" "}
            <span className={cls(f.pnl)}>{fmt(f.pnl)} {f.currency}</span>
          </span>
        ))}
      </div>

      {groups.length > 0 && (
        <div className="space-y-1" data-testid="attribution-groups">
          {groups.map((g) => (
            <div key={g.key} className="flex flex-wrap items-center gap-1.5 border-t border-amber-500/15 pt-1.5"
              data-testid={`attribution-group-${g.platform}-${g.father || "ignoto"}`}>
              <Badge variant="outline" className={`text-[10px] ${CERT[g.certainty] || CERT.ambigua}`}>
                {g.certainty}
              </Badge>
              <span className="font-mono text-[11px] text-white/70">
                {g.platform} · {g.n} trade · <span data-testid={`attribution-group-pnl-${g.key}`} className={cls(g.pnl)}>{fmt(g.pnl)} {g.currency}</span>
                {" → "}{g.father || "padre da decidere"}
              </span>
              {g.father && (
                <Button size="sm" className="ml-auto h-6 px-2 text-[10px]"
                  disabled={busy === `g:${g.key}`} onClick={() => confirmGroup(g)}
                  data-testid={`attribution-confirm-group-${g.platform}-${g.father}`}>
                  conferma il gruppo ({g.n})
                </Button>
              )}
              <p className="w-full text-[10px] leading-snug text-white/40">{g.why}</p>
            </div>
          ))}
        </div>
      )}

      {orphans.slice(0, ROWS).map((o) => (
        <div key={o.id} className="flex flex-wrap items-center gap-1.5 border-t border-amber-500/10 pt-1.5"
          data-testid={`attribution-row-${o.id}`}>
          <span className="font-mono text-[11px] text-white/70">
            {o.platform} · {String(o.market).slice(0, 40)} · {o.direction}
          </span>
          <span data-testid={`attribution-pnl-${o.id}`} className={`font-mono text-[11px] ${cls(o.pnl)}`}>{fmt(o.pnl)} {o.currency}</span>
          <span className="font-mono text-[10px] text-white/35">
            {String(o.closedAt || "").slice(0, 10)}
          </span>
          <span className="ml-auto flex flex-wrap items-center gap-1">
            {rec[o.id]?.father && (
              <Button size="sm" className="h-6 px-2 text-[10px]"
                disabled={busy === `${o.id}:${rec[o.id].father}`}
                onClick={() => assign(o.id, rec[o.id].father, rec[o.id].why)}
                data-testid={`attribution-accept-${o.id}`}>
                conferma {rec[o.id].father}
              </Button>
            )}
            {QUICK.map(([k, label]) => (
              <Button key={k} size="sm" variant="outline" className="h-6 px-2 text-[10px]"
                disabled={busy === `${o.id}:${k}`} onClick={() => assign(o.id, k)}
                data-testid={`attribution-set-${k}-${o.id}`}>
                {label}
              </Button>
            ))}
          </span>
        </div>
      ))}
      {orphans.length > ROWS && (
        <p className="text-[10px] text-white/35" data-testid="attribution-more">
          altri {orphans.length - ROWS} orfani: si confermano dai gruppi qui sopra
        </p>
      )}
    </div>
  );
};
